/**
 * __BOARD_MAPS_KEY__ (2026-10-03) ONE place for the board's Google Maps browser key.
 *
 * Before: the key was typed into Settings -> Google Maps and kept per user
 * (localStorage + profiles.settings.routingConfig), so every computer / login
 * needed it typed again and Route Builder showed Google's grey "This page can't
 * load Google Maps correctly" box when the key was missing or refused.
 *
 * Now: the shared key lives in the database (app_config row
 * 'board_google_maps_key', read and written ONLY through the SECURITY DEFINER
 * functions public.board_maps_key() / public.set_board_maps_key(text), which
 * check the caller is office staff - app_config itself stays locked, it also
 * holds the hub's server secrets). The key is cached in localStorage under an
 * hmgc_ prefix (cleared on logout). getRoutingConfig() prefers the shared key
 * and falls back to the old per-user one, so every caller (route engine,
 * geocoder, fuel stops, maps) follows it.
 *
 * It must be a BROWSER key restricted to https://hauling.hmgc.io/* - never the
 * hub's server key (that one has no website restriction).
 *
 * Exports used by other screens (Fleet Tracking uses ensureGoogleMaps too):
 *   ensureGoogleMaps()      -> Promise<window.google.maps>; rejects Error with
 *                              .code 'NO_KEY' | 'AUTH_FAILED' | 'LOAD_FAILED'
 *   onMapsAuthFailure(cb)   -> unsubscribe fn; cb runs when Google refuses the key
 *   mapsAuthFailed()        -> boolean
 *   loadSharedMapsKey(force)-> Promise<string> ('' when none)
 *   saveSharedMapsKey(key)  -> Promise<{ ok, error }>
 *   getCachedSharedMapsKey()-> string ('' when none)
 *   mapsKeyProblemText(code)-> plain words for the screen
 */
import { supabase } from '../supabaseClient';
import { loadGoogleMapsScript } from './mapService';

export const SHARED_KEY_CACHE = 'hmgc_board_maps_key';
const ROUTING_CONFIG_KEY = 'hmgc_routing_config';

let authFailed = false;
const listeners = new Set();
let loadPromise = null;

const safeGet = (k) => { try { return localStorage.getItem(k) || ''; } catch (e) { return ''; } };
const safeSet = (k, v) => { try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch (e) { /* private mode */ } };

// A browser key looks like AIza + 35 chars. Anything else is a paste mistake.
export const looksLikeBrowserKey = (k) => /^AIza[0-9A-Za-z_-]{30,}$/.test(String(k || '').trim());

export const getCachedSharedMapsKey = () => safeGet(SHARED_KEY_CACHE).trim();

// The old per-user key (Settings -> Google Maps, this account only).
export const getPersonalMapsKey = () => {
  try {
    const raw = safeGet(ROUTING_CONFIG_KEY);
    return raw ? String(JSON.parse(raw).googleMapsApiKey || '').trim() : '';
  } catch (e) { return ''; }
};

// Shared first, personal as the fallback.
export const pickMapsKey = (shared, personal) => (String(shared || '').trim() || String(personal || '').trim());
export const getEffectiveMapsKey = () => pickMapsKey(getCachedSharedMapsKey(), getPersonalMapsKey());

export const loadSharedMapsKey = async (force = false) => {
  if (!force && loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const { data, error } = await supabase.rpc('board_maps_key');
      if (error) { console.warn('shared maps key read failed:', error.message || error); return getCachedSharedMapsKey(); }
      const key = typeof data === 'string' ? data.trim() : '';
      safeSet(SHARED_KEY_CACHE, key);
      return key;
    } catch (e) {
      console.warn('shared maps key read failed:', e);
      return getCachedSharedMapsKey();
    }
  })();
  return loadPromise;
};

export const saveSharedMapsKey = async (key) => {
  const k = String(key || '').trim();
  try {
    const { error } = await supabase.rpc('set_board_maps_key', { p_key: k });
    if (error) return { ok: false, error: error.message || String(error) };
    safeSet(SHARED_KEY_CACHE, k);
    loadPromise = null;
    return { ok: true, error: null };
  } catch (e) {
    return { ok: false, error: (e && e.message) || String(e) };
  }
};

const installAuthHook = () => {
  if (typeof window === 'undefined') return;
  if (window.__hmgcMapsAuthHook) return;
  window.__hmgcMapsAuthHook = true;
  // Google calls this global when it refuses the key (bad key, website not in
  // the key's list, Maps JavaScript API off, billing off).
  window.gm_authFailure = () => {
    authFailed = true;
    console.error('Google Maps refused the browser key (gm_authFailure).');
    listeners.forEach((cb) => { try { cb(); } catch (e) { /* ignore */ } });
  };
};

export const onMapsAuthFailure = (cb) => {
  installAuthHook();
  listeners.add(cb);
  if (authFailed) { try { cb(); } catch (e) { /* ignore */ } }
  return () => listeners.delete(cb);
};

export const mapsAuthFailed = () => authFailed;

const codedError = (code, message) => { const e = new Error(message); e.code = code; return e; };

export const ensureGoogleMaps = async () => {
  installAuthHook();
  if (authFailed) throw codedError('AUTH_FAILED', mapsKeyProblemText('AUTH_FAILED'));
  if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.Map) {
    return window.google.maps;
  }
  await loadSharedMapsKey();
  const key = getEffectiveMapsKey();
  if (!key) throw codedError('NO_KEY', mapsKeyProblemText('NO_KEY'));
  try {
    await loadGoogleMapsScript(key);
  } catch (e) {
    throw codedError('LOAD_FAILED', mapsKeyProblemText('LOAD_FAILED'));
  }
  if (authFailed) throw codedError('AUTH_FAILED', mapsKeyProblemText('AUTH_FAILED'));
  return window.google.maps;
};

export const mapsKeyProblemText = (code) => {
  switch (code) {
    case 'NO_KEY':
      return 'No Google Maps key is set for the board. The owner: open Settings > Google Maps and paste the browser key once - every computer then uses it.';
    case 'AUTH_FAILED':
      return 'Google refused the map key. Check in Google Cloud: the key is a browser key whose website list includes https://hauling.hmgc.io/*, the Maps JavaScript, Directions, Geocoding and Places APIs are enabled for it, and billing is on. Then paste it in Settings > Google Maps and reload.';
    case 'LOAD_FAILED':
      return 'Google Maps could not be loaded (no internet, or something is blocking maps.googleapis.com). Reload to try again.';
    default:
      return 'Google Maps is not available.';
  }
};

const mapsKeyService = {
  ensureGoogleMaps, onMapsAuthFailure, mapsAuthFailed, loadSharedMapsKey, saveSharedMapsKey,
  getCachedSharedMapsKey, getPersonalMapsKey, getEffectiveMapsKey, pickMapsKey, looksLikeBrowserKey,
  mapsKeyProblemText, SHARED_KEY_CACHE,
};
export default mapsKeyService;
